import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { customerService } from "../../services/customer.service";
import { showToast } from "../../lib/toast";
import { ArrowLeft, Printer, FileText, DollarSign } from "lucide-react";
import { formatBusinessDate } from "../../lib/utils";

interface Invoice {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  totalAmount: number;
  paymentStatus: string;
  status: string;
}

interface InvoicePayment {
  id: string;
  amount: number;
  method: string;
  reference?: string;
  createdAt: string;
  invoice: {
    invoiceNumber: string;
    invoiceDate: string;
  };
}

interface CustomerPayment {
  id: string;
  amount: number;
  paymentMethod: string;
  paymentDate: string;
  reference?: string;
  notes?: string;
  allocatedInvoices?: string;
}

interface Customer {
  id: string;
  name: string;
  phone?: string | null;
  email?: string | null;
  address?: string | null;
  creditLimit?: number;
  creditUsed?: number;
}

export function CustomerLedgerPage() {
  const { id } = useParams<{ id: string }>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [customerPayments, setCustomerPayments] = useState<CustomerPayment[]>(
    [],
  );
  const [, setInvoicePayments] = useState<InvoicePayment[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(
    formatBusinessDate(new Date(Date.now() - 90 * 24 * 60 * 60 * 1000)),
  );
  const [endDate, setEndDate] = useState(formatBusinessDate(new Date()));

  useEffect(() => {
    if (id) {
      loadCustomerData();
    }
  }, [id, startDate, endDate]);

  const loadCustomerData = async () => {
    try {
      setLoading(true);
      const [customerData, statsData, paymentsData, invoicesData] =
        await Promise.all([
          customerService.getCustomer(id!),
          customerService.getCustomerStats(id!),
          customerService.getPaymentHistory(id!),
          customerService.getCustomerInvoices(id!, { startDate, endDate }),
        ]);

      setCustomer(customerData);
      setStats(statsData);
      setCustomerPayments(paymentsData.customerPayments || []);
      setInvoicePayments(paymentsData.invoicePayments || []);
      setInvoices(invoicesData || []);
    } catch (error: any) {
      showToast.error(error.response?.data?.message || "Failed to load ledger");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Combine all transactions and sort by date
  const allTransactions = [
    ...invoices.map((inv) => ({
      type: "invoice",
      date: inv.invoiceDate,
      description: `Invoice ${inv.invoiceNumber}`,
      debit: inv.totalAmount,
      credit: 0,
      balance: 0,
      status: inv.paymentStatus,
      data: inv,
    })),
    ...customerPayments.map((pay) => ({
      type: "payment",
      date: pay.paymentDate,
      description: `Payment - ${pay.paymentMethod}${pay.reference ? ` (${pay.reference})` : ""}`,
      debit: 0,
      credit: pay.amount,
      balance: 0,
      status: "PAID",
      data: pay,
    })),
  ].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // Calculate running balance
  let runningBalance = 0;
  allTransactions.forEach((txn) => {
    runningBalance += txn.debit - txn.credit;
    txn.balance = runningBalance;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Loading ledger...</div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="p-6">
        <div className="text-center py-12">Customer not found</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 print:hidden">
        <Link
          to="/customers"
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Customers
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Customer Ledger
            </h1>
            <p className="text-gray-600 mt-1">{customer.name}</p>
          </div>
          <button
            onClick={handlePrint}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Print Ledger
          </button>
        </div>
      </div>

      {/* Customer Info & Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Sales</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{stats?.totalPaidAmount || 0}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Outstanding</p>
              <p className="text-2xl font-bold text-red-600">
                ₹{stats?.totalPendingAmount || 0}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Credit Limit</p>
              <p className="text-2xl font-bold text-gray-900">
                ₹{customer.creditLimit}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Available Credit</p>
              <p className="text-2xl font-bold text-green-600">
                ₹
                {Math.max(
                  0,
                  Number(customer.creditLimit) - Number(customer.creditUsed),
                )}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6 print:hidden">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Ledger Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Debit (₹)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credit (₹)
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance (₹)
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No transactions found for the selected period
                  </td>
                </tr>
              ) : (
                allTransactions.map((txn, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(txn.date).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata",
                      })}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {txn.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-red-600">
                      {txn.debit > 0 ? txn.debit.toFixed(2) : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-green-600">
                      {txn.credit > 0 ? txn.credit.toFixed(2) : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                      {txn.balance.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded-full ${
                          txn.status === "PAID"
                            ? "bg-green-100 text-green-800"
                            : txn.status === "PARTIAL"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-red-100 text-red-800"
                        }`}
                      >
                        {txn.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {allTransactions.length > 0 && (
              <tfoot className="bg-gray-50">
                <tr>
                  <td
                    colSpan={2}
                    className="px-6 py-4 text-sm font-bold text-gray-900"
                  >
                    Closing Balance
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-red-600">
                    {allTransactions
                      .reduce((sum, txn) => sum + txn.debit, 0)
                      .toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-green-600">
                    {allTransactions
                      .reduce((sum, txn) => sum + txn.credit, 0)
                      .toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-gray-900">
                    {runningBalance.toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
          .print\\:hidden {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}
